import { useEffect, useRef, useState } from "react"
import { searchPlaces } from "../services/placesConnector"

/**
 * Google Places autocomplete for a Checker trip's destination, matching the
 * Android app's PlacePickerField. checker_trip.place_label is a plain
 * string server-side, so picking a suggestion just fills the field with its
 * description rather than resolving to a scored Chaperon entity the way the
 * main site's search (SearchBox.jsx) does -- there's no place-details or
 * photo lookup here, only the one autocomplete call, same endpoint/debounce
 * SearchBox already uses, so this adds no new kind of Google Places cost.
 *
 * @param {string} id
 * @param {string} value - The committed place_label; also what's shown typed
 * @param {(value: string) => void} onChange
 */
export default function CheckerPlacePickerField({ id, value, onChange }) {
    const [suggestions, setSuggestions] = useState([])
    // Set right before a selection's own onChange, so the debounce effect
    // below doesn't immediately re-search for the string it just committed.
    const justSelected = useRef(false)
    const blurTimeout = useRef(null)

    useEffect(() => {
        if (justSelected.current) {
            justSelected.current = false
            return
        }
        if (!value || value.trim().length <= 2) {
            setSuggestions([])
            return
        }

        const timeoutId = setTimeout(async () => {
            try {
                const results = await searchPlaces(value.trim())
                setSuggestions(Array.isArray(results) ? results : [])
            } catch {
                setSuggestions([])
            }
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [value])

    useEffect(() => () => clearTimeout(blurTimeout.current), [])

    const selectSuggestion = (description) => {
        justSelected.current = true
        setSuggestions([])
        onChange(description)
    }

    return (
        <div className="checker-place-picker">
            <input
                id={id}
                className="form-control"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onFocus={() => clearTimeout(blurTimeout.current)}
                onBlur={() => {
                    // Delayed so a suggestion's own click still registers
                    // before the list unmounts.
                    blurTimeout.current = setTimeout(() => setSuggestions([]), 150)
                }}
                autoComplete="off"
            />
            {suggestions.length > 0 && (
                <ul className="checker-place-picker-suggestions">
                    {suggestions.map((suggestion) => (
                        <li key={suggestion.place_id}>
                            <button
                                type="button"
                                // Fires before the input's own onBlur closes the list.
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => selectSuggestion(suggestion.description)}
                            >
                                <i className="bi bi-geo-alt me-2" aria-hidden="true"></i>
                                {suggestion.description}
                            </button>
                        </li>
                    ))}
                    <li className="checker-place-picker-attribution">
                        Powered by Google
                    </li>
                </ul>
            )}
        </div>
    )
}
